import {
  Dropzone,
  DropzoneLogoSquare,
} from "@/components/dropzone/Dropzone";
import { useSupabaseUpload } from "@/components/dropzone/hooks";
import LoadingSpinner from "@/components/LoadingSpinner";
import { SignupQuestionnaire } from "@/components/onboarding/SignupQuestionnaire";
import { usePostHog } from "@/components/PostHog/context";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  captureSignupQuestionnaire,
  readSignupQuestionnaireCompleted,
  writeSignupQuestionnaireCompleted,
  type SignupQuestionnaireAnswers,
} from "@/features/onboarding/signupQuestionnaire";
import { applyOrgDtoToSession } from "@/lib/org/applyOrgDtoToSession";
import { store } from "@/lib/states/store";
import { useCoach, useOrg } from "@/lib/states/session/hooks";
import { supabase } from "@/lib/supabase/supabaseClient";
import { trpcVanilla } from "@/lib/trpc/vanilla";
import {
  getReferralCodeFromSearch,
  getStoredReferralCode,
  persistReferralCode,
} from "@/lib/referralCode";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const lockupClassName =
  "h-6 [filter:brightness(0)_saturate(100%)_invert(20%)_sepia(98%)_saturate(3828%)_hue-rotate(219deg)_brightness(103%)_contrast(104%)]";

const easeOut = [0.16, 1, 0.3, 1] as const;
const easeIn = [0.7, 0, 0.84, 0] as const;

const setupScreenVariants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: easeOut },
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: { duration: 0.28, ease: easeIn },
  },
};

function getPricingCurrency() {
  const raw = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith("tr_pricing_currency="));
  if (!raw) return null;
  const value = raw.split("=").slice(1).join("=").toUpperCase();
  return ["USD", "GBP", "EUR", "AUD"].includes(value) ? value : null;
}

function getDetectedTimezone() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZone?.trim() || null;
  } catch {
    return null;
  }
}

export const Route = createFileRoute("/(setup)/setup")({
  component: SetupPage,
  beforeLoad: async ({ context }) => {
    if (!context.session) {
      store.getState().session.reset();
      throw redirect({
        to: "/login",
        replace: true,
      });
    }
  },
  loader: async ({ context }) => {
    const user = context.session?.user;
    return { user };
  },
});

function SetupPage() {
  const { user } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const coach = useCoach();
  const org = useOrg();
  const orgId = org?.id;
  const posthog = usePostHog();
  const [isBootstrapping, setIsBootstrapping] = React.useState(true);
  const [bootstrapError, setBootstrapError] = React.useState<string | null>(null);
  const [bootstrapAttempt, setBootstrapAttempt] = React.useState(0);
  const [questionnaireComplete, setQuestionnaireComplete] = React.useState(
    () => readSignupQuestionnaireCompleted(user?.id),
  );

  React.useEffect(() => {
    let cancelled = false;

    const runBootstrap = async () => {
      setIsBootstrapping(true);
      setBootstrapError(null);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          navigate({ to: "/login", replace: true });
          return;
        }

        const sessionState = store.getState().session;
        if (!sessionState.initialised || sessionState.needsRefreshAfterRehydrate) {
          await sessionState.initWithUserAsync(session.user, { force: true });
        }

        if (!store.getState().session.org) {
          const referralCodeFromSearch = typeof window !== "undefined"
            ? getReferralCodeFromSearch(window.location.search)
            : null;
          if (referralCodeFromSearch) {
            persistReferralCode(referralCodeFromSearch);
          }
          const storedReferralCode = getStoredReferralCode();
          const referralCode = referralCodeFromSearch ?? storedReferralCode;
          const referralSource = referralCode
            ? referralCodeFromSearch
              ? "query"
              : "manual_entry"
            : undefined;

          let hasMembership = false;
          try {
            const bootstrap = await trpcVanilla.auth.getSession.query();
            hasMembership = bootstrap.memberships.length > 0;
          } catch (membershipError) {
            console.warn("Unable to check existing membership", membershipError);
          }

          if (!hasMembership) {
            await trpcVanilla.auth.bootstrap.mutate({
              timezone: getDetectedTimezone(),
              pricingCurrency: getPricingCurrency(),
              referralCode: referralCode ?? undefined,
              referralSource,
            });
          }

          await sessionState.initWithUserAsync(session.user, { force: true });
        }

        const resolvedOrg = store.getState().session.org;
        if (!resolvedOrg) {
          throw new Error("We couldn't load your workspace.");
        }

        if (resolvedOrg.setupComplete) {
          navigate({ to: "/clients", replace: true });
          return;
        }

        if (!cancelled) {
          setIsBootstrapping(false);
        }
      } catch (error) {
        console.error("Setup bootstrap failed", error);
        if (!cancelled) {
          setBootstrapError(
            error instanceof Error ? error.message : "Unable to set up your account.",
          );
          setIsBootstrapping(false);
        }
      }
    };

    void runBootstrap();
    return () => {
      cancelled = true;
    };
  }, [bootstrapAttempt, navigate]);

  const metadata = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const nameFromMetadata = typeof metadata.name === "string"
    ? metadata.name.trim()
    : "";
  const firstNameFromMetadata = typeof metadata.first_name === "string"
    ? metadata.first_name.trim()
    : "";
  const givenNameFromMetadata = typeof metadata.given_name === "string"
    ? metadata.given_name.trim()
    : "";
  const firstNameFromFullName = nameFromMetadata
    ? nameFromMetadata.split(/\s+/)[0]
    : "";
  const firstNameFromEmail = (user?.email ?? "").split("@")[0]?.trim() ?? "";
  const coachFirstName = coach?.firstName?.trim() ||
    firstNameFromMetadata ||
    givenNameFromMetadata ||
    firstNameFromFullName ||
    firstNameFromEmail ||
    "Your";
  const suggestedOrgName = `${coachFirstName}'s Coaching`;

  const formSchema = z.object({
    orgName: z.string().min(1, "Workspace name is required"),
    weightUnit: z.enum(["kg", "lbs"]),
    distanceUnit: z.enum(["km", "mi"]),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      orgName: "",
      weightUnit: "kg",
      distanceUnit: "km",
    },
  });

  const dropZoneProps = useSupabaseUpload({
    bucketName: "profile-images",
    path: orgId ?? "",
    allowedMimeTypes: ["image/*"],
    maxFiles: 1,
    maxFileSize: 5000000, // 5MB
    upsert: true,
    canCrop: true,
  });

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!orgId) {
      console.error("No orgId");
      return;
    }
    const logoFile = dropZoneProps.files[0];
    let logoPath: string | undefined;
    let logoVersion: number | undefined;
    let logoUploadOk = false;

    if (logoFile) {
      try {
        const signed = await trpcVanilla.org.createLogoUpload.mutate();
        const { error: uploadError } = await supabase.storage
          .from(signed.bucket)
          .uploadToSignedUrl(signed.path, signed.token, logoFile);
        if (uploadError) {
          console.error("Logo upload failed", uploadError);
          toast.error("Logo upload failed. You can try again in settings.");
        } else {
          logoPath = signed.path;
          logoVersion = 1;
          logoUploadOk = true;
        }
      } catch (uploadError) {
        console.error("Logo upload failed", uploadError);
        toast.error("Logo upload failed. You can try again in settings.");
      }
    }

    try {
      const updated = await trpcVanilla.org.updateProfile.mutate({
        name: values.orgName,
        weightUnitDefault: values.weightUnit,
        distanceUnitDefault: values.distanceUnit,
        setupComplete: true,
        ...(logoUploadOk && logoPath
          ? { logoPath, logoVersion }
          : {}),
      });
      applyOrgDtoToSession(updated);
    } catch (error) {
      console.error(error);
      toast.error("Error updating workspace");
      return;
    }

    navigate({ to: "/getting-started", replace: true });
  };

  const handleQuestionnaireComplete = (answers: SignupQuestionnaireAnswers) => {
    captureSignupQuestionnaire(posthog, answers);
    if (user?.id) {
      writeSignupQuestionnaireCompleted(user.id);
    }
    setQuestionnaireComplete(true);
  };

  const setupScreen =
    !questionnaireComplete ? (
      <SignupQuestionnaire onComplete={handleQuestionnaireComplete} />
    ) : bootstrapError ? (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Account setup failed</CardTitle>
              <CardDescription>{bootstrapError}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full"
                onClick={() => setBootstrapAttempt((attempt) => attempt + 1)}
              >
                Try again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    ) : isBootstrapping ? (
      <div className="flex min-h-svh flex-col items-center justify-center bg-background px-6">
        <div className="flex flex-col items-center gap-6 text-center">
          <img
            src="/lockupBlack.svg"
            alt="Trainrr"
            className="h-7 [filter:brightness(0)_saturate(100%)_invert(20%)_sepia(98%)_saturate(3828%)_hue-rotate(219deg)_brightness(103%)_contrast(104%)]"
          />
          <div
            className="size-9 animate-spin rounded-full border-[3px] border-blue-100 border-t-blue-600"
            aria-hidden
          />
          <div className="space-y-1">
            <p className="text-base font-medium tracking-tight">
              Finishing account setup
            </p>
            <p className="text-sm text-muted-foreground">
              This usually only takes a moment.
            </p>
          </div>
        </div>
      </div>
    ) : (
      <div className="flex min-h-svh flex-col bg-background">
        <header className="flex items-center px-6 py-5 md:px-10">
          <img
            src="/lockupBlack.svg"
            alt="Trainrr"
            className={lockupClassName}
          />
        </header>
        <main className="mx-auto flex w-full max-w-lg flex-1 flex-col px-6 pb-10 md:px-10">
          <div className="mb-8 space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight">
              Set up your workspace
            </h1>
            <p className="text-muted-foreground">
              This is what clients will see.
            </p>
          </div>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-8"
            >
              <FormField
                control={form.control}
                name="orgName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Workspace name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={suggestedOrgName}
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-2">
                <p className="text-sm font-medium">Logo</p>
                <Dropzone
                  {...dropZoneProps}
                  className="cursor-pointer overflow-hidden p-0"
                >
                  <DropzoneLogoSquare />
                </Dropzone>
                <p className="text-sm text-muted-foreground">
                  Optional. You can add this later in settings.
                </p>
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="weightUnit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Weight</FormLabel>
                      <FormControl>
                        <ToggleGroup
                          type="single"
                          value={field.value}
                          onValueChange={(value) => {
                            if (value) field.onChange(value);
                          }}
                          variant="outline"
                          className="grid w-full grid-cols-2 gap-0 rounded-lg border bg-muted/50 p-1"
                        >
                          <ToggleGroupItem
                            value="kg"
                            className="rounded-md border-0 data-[state=on]:bg-background data-[state=on]:shadow-sm"
                          >
                            kg
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="lbs"
                            className="rounded-md border-0 data-[state=on]:bg-background data-[state=on]:shadow-sm"
                          >
                            lbs
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="distanceUnit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Distance</FormLabel>
                      <FormControl>
                        <ToggleGroup
                          type="single"
                          value={field.value}
                          onValueChange={(value) => {
                            if (value) field.onChange(value);
                          }}
                          variant="outline"
                          className="grid w-full grid-cols-2 gap-0 rounded-lg border bg-muted/50 p-1"
                        >
                          <ToggleGroupItem
                            value="km"
                            className="rounded-md border-0 data-[state=on]:bg-background data-[state=on]:shadow-sm"
                          >
                            km
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            value="mi"
                            className="rounded-md border-0 data-[state=on]:bg-background data-[state=on]:shadow-sm"
                          >
                            mi
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button
                className="w-full"
                size="lg"
                disabled={!form.formState.isValid}
              >
                {form.formState.isSubmitting ? <LoadingSpinner /> : "Get started"}
                {!form.formState.isSubmitting ? <ChevronRight /> : null}
              </Button>
            </form>
          </Form>
        </main>
      </div>
    );

  const setupScreenKey = !questionnaireComplete
    ? "questionnaire"
    : bootstrapError
      ? "error"
      : isBootstrapping
        ? "bootstrapping"
        : "org-form";

  return (
    <div className="min-h-svh overflow-hidden bg-background">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={setupScreenKey}
          className="min-h-svh"
          variants={setupScreenVariants}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          {setupScreen}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
